import Typography from '@mui/material/Typography';
import { useBlockingSubscription } from 'features/user';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Divider } from 'shared/components/ui/divider';
import { FormInput } from 'shared/components/ui/form-input';
import { DeleteAccountDialog } from 'shared/dialogs/delete-account';

import * as Styled from './settings-form.style';
import { SettingsInfoIcon } from './settings-info-icon';
import { UserName } from './user-name';

export type AccountSettingsProps = {
  name: string;
  onChangeName: (name: string) => void;
  email: string;
  isSignedInWithProvider?: boolean;
  onChangePassword: () => void;
};

export const AccountSettings: React.FC<AccountSettingsProps> = ({
  name,
  onChangeName,
  email,
  isSignedInWithProvider,
  onChangePassword,
}) => {
  const { t } = useTranslation();
  const blockingSubscription = useBlockingSubscription();
  const [isDeleteAccountOpen, setDeleteAccountOpen] = useState(false);

  const onOpenDeleteAccount = () => setDeleteAccountOpen(true);
  const onCloseDeleteAccount = () => setDeleteAccountOpen(false);

  return (
    <>
      <Styled.Section>
        <Styled.SettingItem>
          <Styled.SettingLabel htmlFor="name">
            {t('forms.user-settings.sections.name.label')}
          </Styled.SettingLabel>

          <UserName id="name" value={name} onChange={onChangeName} />
        </Styled.SettingItem>

        <Styled.SettingItem>
          <Styled.SettingLabel htmlFor="email">
            {t('forms.user-settings.sections.security.email.title')}
          </Styled.SettingLabel>

          <FormInput id="email" value={email} disabled />
        </Styled.SettingItem>

        <Styled.SettingItem $autoWidthField>
          <Styled.LabelContainer>
            <Typography variant="body2" component="span">
              {t('forms.user-settings.sections.security.password.label')}
            </Typography>
          </Styled.LabelContainer>

          {isSignedInWithProvider ? (
            <Styled.SecondaryTextTypography variant="body2">
              {t('forms.user-settings.sections.security.siwa.title')}
            </Styled.SecondaryTextTypography>
          ) : (
            <Button
              onClick={onChangePassword}
              size={Sizes.Small}
              variant={Variants.Contained}
              color={Colors.White}
            >
              {t('forms.user-settings.sections.security.password.button')}
            </Button>
          )}
        </Styled.SettingItem>
      </Styled.Section>

      <Divider />

      <Styled.Section>
        <Styled.SettingItem $autoWidthField>
          <Styled.LabelContainer>
            <Typography variant="body2" component="span">
              {t('forms.user-settings.sections.delete-account.label')}
            </Typography>
            <SettingsInfoIcon
              label={t(
                'forms.user-settings.sections.delete-account.description',
              )}
            />
          </Styled.LabelContainer>

          <Button
            size={Sizes.Small}
            variant={Variants.Outlined}
            color={Colors.Error}
            onClick={onOpenDeleteAccount}
          >
            {t('forms.user-settings.sections.delete-account.button.label')}
          </Button>
        </Styled.SettingItem>
      </Styled.Section>

      <DeleteAccountDialog
        open={isDeleteAccountOpen}
        blockingSubscription={blockingSubscription}
        onClose={onCloseDeleteAccount}
      />
    </>
  );
};
